import { useState } from 'react';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteConfig, reviews } from '@/mocks/animeData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9q87fue3oq4jqumthsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@example.com',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    description: `了解${siteConfig.name}的品牌故事、用户评价与联系方式`,
    url: `${siteConfig.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`关于我们｜${siteConfig.name}`}
        description={`了解${siteConfig.name}的品牌介绍、用户口碑评价与联系方式。致力于为成人动漫爱好者提供高质量推荐与导航服务。`}
        keywords="成人动漫,关于我们,联系我们,成人动漫推荐"
        canonicalUrl={`${siteConfig.url}/about`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '关于我们' },
              ]}
            />
          </div>

          <section className="mb-16">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">关于我们</h1>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <h2 className="font-heading text-xl text-foreground-900 mb-4">品牌介绍</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                成人动漫精选推荐成立于2024年，是一个专注于成人动漫领域的资源导航与推荐平台。我们致力于为成人动漫爱好者提供最全面、最专业的作品推荐、类型解析与观看指南。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                在信息爆炸的时代，找到真正值得花时间观看的成人动漫作品变得越来越困难。我们的编辑团队由资深成人动漫爱好者组成，凭借多年的观看经验和对行业的深刻理解，为你精心筛选每一部推荐作品。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                我们的使命是：让每一位成人动漫爱好者都能轻松找到适合自己的高质量作品，节省筛选时间，提升观看体验。无论你是刚入门的新手还是资深老粉，都能在这里找到有价值的内容。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">用户评价</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {reviews.map((review, index) => (
                <div key={index} className="bg-background-100 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: review.rating }, (_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.comment}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">联系我们</h2>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
                如果你有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。我们会在48小时内回复你的消息。
              </p>

              <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5 max-w-lg">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入你的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <div className="honeypot-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'loading' ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600">留言已成功提交，我们会尽快回复你！</p>
                )}
                {formStatus === 'error' && formError && (
                  <p className="text-sm text-primary-500">{formError}</p>
                )}
              </form>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">其他联系方式</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500">contact@example.com</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-wechat-line text-xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-500">成人动漫精选推荐</p>
              </div>
              <div className="bg-background-100 rounded-xl p-5 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-secondary-100 flex items-center justify-center">
                  <i className="ri-twitter-x-line text-xl text-secondary-500"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">社交媒体</h3>
                <p className="text-xs text-foreground-500">@anime_recommend</p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}